"use client";

import { useState, useEffect, useCallback, useRef } from "react";

export interface FinanceCompany {
  name: string;
  ticker: string;
  exchange: string;
  color: string;
  sector: string;
  marketCap?: string;
  marketCapRaw?: number;
  keyMetric?: string;
  recentNews?: string;
}

export interface FinanceData {
  comparisonType: string;
  metric: string;
  metricUnit: string;
  timeframe?: string;
  sectors?: { name: string; color: string; companyCount: number }[];
  companies?: FinanceCompany[];
  upcomingEvents?: { event: string; date: string; company: string; impact: string }[];
  historicalNote?: string;
}

export interface GeopoliticsData {
  conflictType: string;
  escalationLevel: string;
  involvedParties?: { name: string; flagCode: string; role: string; description?: string }[];
  recentEvents?: { event: string; date: string; significance: string }[];
  keyConcerns?: string[];
  timeline?: { event: string; date: string; isPast: boolean }[];
  historicalNote?: string;
}

export interface CryptoData {
  assets?: { name: string; symbol: string; color: string; currentPrice?: string; priceTarget?: string; marketCap?: string }[];
  metric: string;
  keyLevels?: { level: string; label: string; type: string }[];
  catalysts?: { event: string; date: string; impact: string; asset?: string }[];
  marketContext?: string;
  historicalNote?: string;
}

export interface AwardsData {
  awardShow: string;
  ceremony?: { date: string; venue?: string; broadcaster?: string; edition?: string };
  category?: string;
  nominees?: { name: string; work?: string; creator?: string; previousWins?: number; color: string; odds?: string }[];
  historicalNote?: string;
}

export interface WeatherRange {
  label: string;
  min: number | null;
  max: number | null;
  marketQuestion: string;
}

export interface WeatherData {
  location: {
    city: string;
    region?: string;
    country: string;
    countryCode: string;
    latitude: number;
    longitude: number;
    timezone: string;
  };
  targetDate: string;
  metric: string;
  metricLabel: string;
  unit: string;
  ranges: WeatherRange[];
  historicalNote?: string;
}

export interface PoliticsData {
  country: string;
  countryCode: string;
  electionLevel: string;
  electoralSystem: string;
  electionName: string;
  winCondition?: string;
  keyRegions?: { name: string; importance: string; description?: string }[];
  timeline?: { event: string; date: string; isPast: boolean }[];
  parties?: { name: string; fullName: string; color: string; lean: string }[];
  historicalNote?: string;
}

// Types matching the API response from /api/ai/analyze-context
export interface ContextAnalysis {
  category: "music" | "sports" | "politics" | "gaming" | "crypto" | "entertainment" | "science" | "business" | "other";
  contextType:
    | "spotify_songs"
    | "spotify_artists"
    | "sports_match"
    | "sports_league"
    | "politics_election"
    | "politics_policy"
    | "geopolitics_conflict"
    | "gaming_awards"
    | "gaming_esports"
    | "crypto_price"
    | "crypto_market"
    | "entertainment_awards"
    | "awards_ceremony"
    | "finance_companies"
    | "weather_forecast"
    | "xtracker"
    | "generic";
  confidence: number;
  entities: {
    type: "song" | "artist" | "team" | "player" | "candidate" | "game" | "crypto" | "company" | "person" | "other";
    name: string;
    metadata?: Record<string, string>;
  }[];
  dataSources: {
    api: "spotify" | "espn" | "twitter" | "coingecko" | "wikipedia" | "none";
    endpoint?: string;
    query?: string;
  }[];
  widgetConfig: {
    title: string;
    subtitle?: string;
    showRankings: boolean;
    rankingMetric?: string;
    showStats: boolean;
    primaryColor?: string;
  };
  displayHints: {
    icon?: string;
    theme?: "spotify" | "sports" | "politics" | "gaming" | "crypto" | "finance" | "geopolitics" | "awards" | "weather" | "default";
    showLeaderboard: boolean;
    showTimeline: boolean;
    showExternalLinks: boolean;
  };
  politicsData?: PoliticsData;
  financeData?: FinanceData;
  geopoliticsData?: GeopoliticsData;
  cryptoData?: CryptoData;
  awardsData?: AwardsData;
  weatherData?: WeatherData;
  reasoning: string;
}

interface UseAIContextAnalysisOptions {
  eventSlug: string;
  eventTitle: string;
  eventDescription?: string;
  marketQuestions?: string[];
  enabled?: boolean;
}

interface UseAIContextAnalysisReturn {
  analysis: ContextAnalysis | null;
  loading: boolean;
  error: string | null;
  cached: boolean;
  refetch: () => Promise<void>;
}

export function useAIContextAnalysis({
  eventSlug,
  eventTitle,
  eventDescription,
  marketQuestions,
  enabled = true,
}: UseAIContextAnalysisOptions): UseAIContextAnalysisReturn {
  const [analysis, setAnalysis] = useState<ContextAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cached, setCached] = useState(false);

  // Prevent duplicate fetches and infinite loops
  const fetchedRef = useRef<string | null>(null);
  const errorCountRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  // Stabilize marketQuestions to prevent infinite loops
  const marketQuestionsKey = JSON.stringify(marketQuestions || []);

  const fetchAnalysis = useCallback(async (force = false) => {
    if (!eventSlug || !eventTitle || !enabled) return;

    // Prevent duplicate fetches for same slug (unless forced)
    const fetchKey = `${eventSlug}-${eventTitle}`;
    if (!force && fetchedRef.current === fetchKey) return;

    // Prevent infinite error loops (max 2 retries)
    if (!force && errorCountRef.current >= 2) {
      return;
    }

    // Abort any in-flight request for a previous slug
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      // POST handles cache check + generation in one call (backend checks DB cache first)
      const res = await fetch("/api/ai/analyze-context", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          eventSlug,
          eventTitle,
          eventDescription: eventDescription || "",
          marketQuestions: marketQuestions || [],
        }),
        signal: controller.signal,
      });

      const data = await res.json();

      // Handle rate limit or error with fallback cache
      if (data.success && data.analysis) {
        setAnalysis(data.analysis);
        setCached(data.cached || false);
        fetchedRef.current = fetchKey;
        errorCountRef.current = 0;
      } else if (!res.ok) {
        // Increment error count to prevent infinite loops
        errorCountRef.current++;
        // Rate limit / quota exceeded — stop retrying silently
        if (res.status === 429 || data.rateLimited) {
          errorCountRef.current = 2; // Max out to prevent further retries
          fetchedRef.current = fetchKey;
          return;
        }
        throw new Error(data.error || "Failed to analyze context");
      } else {
        errorCountRef.current++;
        throw new Error("Invalid response from analysis API");
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Failed to analyze event context";
      setError(errorMessage);
      // Don't clear analysis if we have one - keep showing stale data
      if (!analysis) {
        setAnalysis(null);
      }
    } finally {
      setLoading(false);
    }
  }, [eventSlug, eventTitle, eventDescription, marketQuestionsKey, enabled, analysis]);

  useEffect(() => {
    // Reset error count when slug changes
    if (eventSlug !== fetchedRef.current?.split("-")[0]) {
      errorCountRef.current = 0;
    }
    // Short debounce to avoid rapid-fire requests when navigating quickly
    const timer = setTimeout(() => {
      fetchAnalysis();
    }, 100);
    return () => {
      clearTimeout(timer);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [eventSlug, eventTitle, enabled]); // Intentionally exclude fetchAnalysis to prevent loops

  return {
    analysis,
    loading,
    error,
    cached,
    refetch: () => fetchAnalysis(true),
  };
}

// Helper functions for working with analysis data
export function getThemeColors(theme: ContextAnalysis["displayHints"]["theme"]) {
  switch (theme) {
    case "spotify":
      return {
        primary: "#1DB954",
        secondary: "#1ed760",
        bg: "from-[#1DB954]/10 via-[#18181D] to-[#0F0F12]",
        border: "[#1DB954]/30",
        text: "[#1DB954]",
      };
    case "sports":
      return {
        primary: "#013369", // NFL blue
        secondary: "#D50A0A",
        bg: "from-[#013369]/10 via-[#18181D] to-[#0F0F12]",
        border: "[#013369]/30",
        text: "[#00A3E0]",
      };
    case "politics":
      return {
        primary: "#1976D2",
        secondary: "#D32F2F",
        bg: "from-[#1976D2]/10 via-[#18181D] to-[#0F0F12]",
        border: "[#1976D2]/30",
        text: "[#1976D2]",
      };
    case "gaming":
      return {
        primary: "#9146FF",
        secondary: "#FF4500",
        bg: "from-[#9146FF]/10 via-[#18181D] to-[#0F0F12]",
        border: "[#9146FF]/30",
        text: "[#9146FF]",
      };
    case "crypto":
      return {
        primary: "#F7931A", // Bitcoin orange
        secondary: "#627EEA", // Ethereum blue
        bg: "from-[#F7931A]/10 via-[#18181D] to-[#0F0F12]",
        border: "[#F7931A]/30",
        text: "[#F7931A]",
      };
    case "finance":
      return {
        primary: "#10B981",
        secondary: "#3B82F6",
        bg: "from-[#10B981]/10 via-[#18181D] to-[#0F0F12]",
        border: "[#10B981]/30",
        text: "[#10B981]",
      };
    case "geopolitics":
      return {
        primary: "#EF4444",
        secondary: "#F97316",
        bg: "from-[#EF4444]/10 via-[#18181D] to-[#0F0F12]",
        border: "[#EF4444]/30",
        text: "[#EF4444]",
      };
    case "awards":
      return {
        primary: "#F59E0B",
        secondary: "#EAB308",
        bg: "from-[#F59E0B]/10 via-[#18181D] to-[#0F0F12]",
        border: "[#F59E0B]/30",
        text: "[#F59E0B]",
      };
    case "weather":
      return {
        primary: "#38BDF8",
        secondary: "#F59E0B",
        bg: "from-[#38BDF8]/10 via-[#18181D] to-[#0F0F12]",
        border: "[#38BDF8]/30",
        text: "[#38BDF8]",
      };
    default:
      return {
        primary: "#CCFF00",
        secondary: "#B5FF2A",
        bg: "from-[#CCFF00]/10 via-[#18181D] to-[#0F0F12]",
        border: "[#CCFF00]/30",
        text: "[#CCFF00]",
      };
  }
}

export function getIconName(icon?: string): string {
  switch (icon) {
    case "music":
      return "Music";
    case "trophy":
      return "Trophy";
    case "vote":
      return "Vote";
    case "gamepad":
      return "Gamepad2";
    case "bitcoin":
      return "Bitcoin";
    case "star":
      return "Star";
    case "twitter":
      return "Twitter";
    case "thermometer":
      return "Thermometer";
    default:
      return "Sparkles";
  }
}
